import { Link, useRouterState } from "@tanstack/react-router";
import { User, Package, Menu, ArrowLeft } from "lucide-react";
import { useEffect, useState } from "react";

import { useAuth } from "@/hooks/use-auth";
import { supabase } from "@/integrations/supabase/client";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle, SheetClose } from "@/components/ui/sheet";
import julsLogo from "@/assets/juls-logo.webp.asset.json";

type NavLink = {
  id: string;
  label: string;
  path: string;
  search_key: string | null;
  search_value: string | null;
  enabled: boolean;
  position: number;
};

export function SiteHeader() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [navLinks, setNavLinks] = useState<NavLink[]>([]);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const showBack = pathname !== "/" && pathname !== "/shop" && !pathname.startsWith("/account") && !pathname.startsWith("/auth") && !pathname.startsWith("/orders") && !pathname.startsWith("/cart") && !pathname.startsWith("/checkout");

  useEffect(() => {
    supabase
      .from("nav_links")
      .select("*")
      .eq("enabled", true)
      .order("position", { ascending: true })
      .then(({ data }) => setNavLinks((data as NavLink[]) ?? []));
  }, []);

  const renderLink = (l: NavLink, className: string, onClick?: () => void) => {
    const search = l.search_key && l.search_value ? { [l.search_key]: l.search_value } : undefined;
    return (
      <Link
        key={l.id}
        to={l.path as any}
        search={search as any}
        className={className}
        onClick={onClick}
      >
        {l.label}
      </Link>
    );
  };

  return (
    <>
    <header className="sticky top-0 z-40 bg-background/85 backdrop-blur border-b border-border">
      <div className="mx-auto max-w-7xl px-6 h-16 flex items-center justify-between">
        <nav className="flex items-center gap-8">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger className="md:hidden -ml-1 p-1" aria-label="Open menu">
              <Menu className="h-5 w-5" />
            </SheetTrigger>
            <SheetContent side="left" className="w-72">
              <SheetHeader>
                <SheetTitle className="display text-2xl tracking-tight text-left">Jul,s Skin</SheetTitle>
              </SheetHeader>
              <div className="mt-6 flex flex-col gap-1 text-base">
                <SheetClose asChild><Link to="/" className="py-2 hover:opacity-60">Home</Link></SheetClose>
                <SheetClose asChild><Link to="/shop" className="py-2 hover:opacity-60">All Products</Link></SheetClose>
                {navLinks.map((l) => (
                  <SheetClose key={l.id} asChild>
                    {renderLink(l, "py-2 hover:opacity-60")}
                  </SheetClose>
                ))}
                <div className="my-3 h-px bg-border" />
                {user ? (
                  <>
                    <SheetClose asChild><Link to="/account" className="py-2 flex items-center gap-2 hover:opacity-60"><User className="h-4 w-4"/>Account</Link></SheetClose>
                    <SheetClose asChild><Link to="/orders" className="py-2 flex items-center gap-2 hover:opacity-60"><Package className="h-4 w-4"/>Orders</Link></SheetClose>
                  </>
                ) : (
                  <SheetClose asChild><Link to="/auth" className="py-2 flex items-center gap-2 hover:opacity-60"><User className="h-4 w-4"/>Sign in</Link></SheetClose>
                )}
              </div>
            </SheetContent>
          </Sheet>
          <Link to="/" className="display text-2xl tracking-tight">Jul,s Skin</Link>
          <div className="hidden md:flex items-center gap-6 text-sm">
            <Link to="/" activeOptions={{ exact: true }} activeProps={{ className: "font-medium" }} className="hover:opacity-60 transition-opacity">Home</Link>
            <Link to="/shop" activeOptions={{ exact: true }} activeProps={{ className: "font-medium" }} className="hover:opacity-60 transition-opacity">All Products</Link>
            {navLinks.map((l) => renderLink(l, "hover:opacity-60 transition-opacity"))}
            {user && (
              <Link to="/orders" className="hover:opacity-60 transition-opacity">Orders</Link>
            )}
          </div>
        </nav>
        <div className="flex items-center gap-5 text-sm">
          {user && (
            <Link to="/orders" className="hidden sm:flex items-center gap-1 hover:opacity-60"><Package className="h-4 w-4"/>Orders</Link>
          )}
          {user ? (
            <Link to="/account" className="hidden sm:flex items-center gap-1 hover:opacity-60"><User className="h-4 w-4"/>Account</Link>
          ) : (
            <Link to="/auth" className="hidden sm:flex items-center gap-1 hover:opacity-60"><User className="h-4 w-4"/>Sign in</Link>
          )}
          <Link to="/" aria-label="Jul's Skin & Hair Care" className="shrink-0">
            <img
              src={julsLogo.url}
              alt="Jul's Skin & Hair Care"
              width={36}
              height={36}
              decoding="async"
              className="h-9 w-9 rounded-full object-cover ring-1 ring-border"
            />

          </Link>
        </div>
      </div>
    </header>
    {showBack && (
      <div className="bg-background">
        <div className="mx-auto max-w-7xl px-6 py-3">
          <Link to="/shop" className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase hover:opacity-60">
            <ArrowLeft className="h-3.5 w-3.5" /> Shop
          </Link>
        </div>
      </div>
    )}
    </>
  );
}
